import React from 'react';
import { Loader2, Save, ClipboardList, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';
import { moduleApi } from '../../lib/api';
import { Button } from '../ui/Button';
import { DerouleEditor } from '../ui/DerouleEditor';
import { useDossierContext } from './SuiviPrivatisationLayout';

interface BriefQuestion {
    id: string;
    question: string;
    type: 'text' | 'select' | 'datetime' | 'deroule';
    options: { id: string; label: string }[];
    allow_multiple: boolean;
    required: boolean;
    help_text?: string;
}

/**
 * Onglet « Brief » — les informations détaillées récoltées auprès du client avant de chiffrer.
 *
 * AUCUNE QUESTION N'EST CODÉE ICI. Elles viennent du modèle de Démarche que l'établissement a désigné comme
 * son brief, dans Paramètres > Démarches. C'est délibéré : « je ne sais pas quelles questions elles posent »
 * (Emeni, 2026-08-26) — ce sont ses commerciales qui les connaissent, et elles doivent pouvoir les écrire
 * elles-mêmes sans passer par un développeur. Le moteur de questionnaire existait déjà, complet ; le
 * réutiliser évitait d'en écrire un second qui aurait divergé du premier.
 *
 * Les réponses sont enregistrées au format exact des Démarches, pour qu'un brief rempli ici par la
 * commerciale et un brief rempli par le client lui-même produisent la même donnée.
 */
export function DossierBriefPage() {
    const { dossier } = useDossierContext();
    const [chargement, setChargement] = React.useState(true);
    const [modele, setModele] = React.useState<{ id: string; name: string; questions: BriefQuestion[] } | null>(null);
    const [reponses, setReponses] = React.useState<Record<string, string>>({});
    const [maj, setMaj] = React.useState<{ at: string | null; par: string }>({ at: null, par: '' });
    const [enregistrement, setEnregistrement] = React.useState(false);
    const [erreur, setErreur] = React.useState('');
    const [enregistre, setEnregistre] = React.useState(false);

    React.useEffect(() => {
        moduleApi.getDossierBrief(dossier.dossier_id)
            .then((r: any) => {
                setModele(r?.template || null);
                const map: Record<string, string> = {};
                (r?.answers || []).forEach((a: any) => { map[a.id] = a.answer || ''; });
                setReponses(map);
                setMaj({ at: r?.updated_at || null, par: r?.updated_by_label || '' });
            })
            .catch((e: any) => setErreur(e?.message || 'Chargement impossible.'))
            .finally(() => setChargement(false));
    }, [dossier.dossier_id]);

    const modifier = (id: string, valeur: string) => {
        setReponses((prev) => ({ ...prev, [id]: valeur }));
        setEnregistre(false);
    };

    const enregistrer = async () => {
        if (!modele) return;
        setEnregistrement(true);
        setErreur('');
        try {
            // On renvoie TOUTES les questions du modèle, y compris celles restées vides : sinon une réponse
            // effacée resterait celle d'avant, puisque l'enregistrement remplace l'ensemble.
            const answers = modele.questions.map((q) => ({
                id: q.id,
                question: q.question,
                type: q.type,
                answer: reponses[q.id] || '',
            }));
            await moduleApi.saveDossierBrief(dossier.dossier_id, { template_id: modele.id, answers });
            setEnregistre(true);
        } catch (e: any) {
            setErreur(e?.message || "Le brief n'a pas pu être enregistré.");
        } finally {
            setEnregistrement(false);
        }
    };

    if (chargement) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-content-muted" size={22} /></div>;

    if (!modele) {
        return (
            <div className="rounded-xl border border-dashed border-border p-5 text-center space-y-2">
                <ClipboardList size={22} className="mx-auto text-content-muted" />
                <p className="text-sm font-bold text-content">Aucun questionnaire de brief n'est encore défini</p>
                <p className="text-[11px] text-content-muted max-w-md mx-auto leading-relaxed">
                    Les questions du brief sont les vôtres : celles que vous posez déjà au client pour établir un
                    devis. Créez-les dans Paramètres &gt; Démarches, puis cochez
                    « Utiliser ces questions comme brief de privatisation ».
                </p>
                <Link to="/parametres" className="inline-flex items-center gap-1.5 text-[11px] font-bold text-brand-600 dark:text-brand-400 hover:underline">
                    <Settings size={12} /> Ouvrir les Paramètres
                </Link>
            </div>
        );
    }

    return (
        <div className="space-y-4 max-w-2xl">
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <p className="text-[11px] text-content-muted">
                    Questionnaire : <span className="font-bold text-content">{modele.name}</span>
                </p>
                {maj.at && (
                    <p className="text-[10px] text-content-muted">
                        Dernière mise à jour {new Date(maj.at).toLocaleDateString('fr-FR')}{maj.par ? ` par ${maj.par}` : ''}
                    </p>
                )}
            </div>

            {modele.questions.length === 0 && (
                <p className="text-xs text-content-muted">Ce questionnaire ne contient encore aucune question.</p>
            )}

            {modele.questions.map((q) => (
                <div key={q.id} className="space-y-1.5">
                    <label className="text-[11px] font-bold text-content flex items-baseline gap-1">
                        {q.question}
                        {q.required && <span className="text-red-400">*</span>}
                    </label>
                    {q.help_text && <p className="text-[10px] text-content-muted">{q.help_text}</p>}

                    {q.type === 'deroule' ? (
                        <div className="bg-surface-soft border border-border rounded-xl p-3">
                            <DerouleEditor valeur={reponses[q.id] || ''} onChange={(t) => modifier(q.id, t)} />
                        </div>
                    ) : q.type === 'select' ? (
                        // Les options viennent du modèle ; on n'en invente aucune. Le champ reste libre en
                        // secours si le client répond à côté de la liste — une conversation téléphonique ne
                        // tient pas toujours dans les cases prévues.
                        <select value={reponses[q.id] || ''} onChange={(e) => modifier(q.id, e.target.value)}
                            className="w-full bg-surface border border-border rounded-lg px-2.5 py-1.5 text-sm text-content outline-none focus:border-brand-500">
                            <option value="">—</option>
                            {q.options.map((o) => <option key={o.id} value={o.label}>{o.label}</option>)}
                        </select>
                    ) : q.type === 'datetime' ? (
                        <input type="datetime-local" value={reponses[q.id] || ''} onChange={(e) => modifier(q.id, e.target.value)}
                            className="w-full bg-surface border border-border rounded-lg px-2.5 py-1.5 text-sm text-content outline-none focus:border-brand-500" />
                    ) : (
                        <textarea value={reponses[q.id] || ''} onChange={(e) => modifier(q.id, e.target.value)} rows={2}
                            className="w-full bg-surface border border-border rounded-lg px-2.5 py-1.5 text-sm text-content outline-none focus:border-brand-500 resize-none" />
                    )}
                </div>
            ))}

            {erreur && <p className="text-xs text-red-500 font-semibold">{erreur}</p>}

            <div className="flex items-center gap-3">
                <Button variant="primary" size="sm" onClick={enregistrer} disabled={enregistrement}>
                    {enregistrement ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Enregistrer le brief
                </Button>
                {enregistre && <span className="text-[11px] font-bold text-green-500">Enregistré.</span>}
            </div>
        </div>
    );
}
